// Batch-2 SEO location pages: district courts (2026-07-10).
// See docs/plans/2026-07-10-local-court-seo-pages.md. Court-complex names in `highlights`
// are sourced from src/lib/seo/district-complexes.json (harvested from eCourts); governing
// High Courts follow src/lib/courts/location-metadata.ts. Merged into `locations` in
// locations.ts so the /[locationSlug] template, sitemap and helpers pick them up.
import type { LocationData } from './locations';

const ECOURTS_DISTRICT_PORTAL = 'https://districts.ecourts.gov.in';

export const locationsBatch2: LocationData[] = [
  {
    slug: "gorakhpur-court-case-tracking",
    cityName: "Gorakhpur",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Gorakhpur District Court cases and never miss a hearing. Get alerts for hearing dates, case-status changes and new orders from the District & Sessions Court, Gorakhpur and the civil and criminal courts under it, on WhatsApp and email in your language.",
    highlights: [
      "District & Sessions Court, Gorakhpur",
      "Commercial Court, Gorakhpur",
      "Motor Accident Claims Tribunal (MACT), Gorakhpur",
      "Outlying court at Bansgaon",
    ],
    courtInfo: {
      name: "Gorakhpur District Courts",
      jurisdiction: "Gorakhpur District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Gorakhpur Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Gorakhpur district court case status online by CNR, case number or party name. Track the District & Sessions Court and the civil, criminal, commercial and MACT courts with WhatsApp & email alerts.",
    seoH1: "Gorakhpur District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Gorakhpur — case tracking",
    altNames: ["Gorakhpur Sessions Court Case Status", "Gorakhpur District Court CNR Status"],
    searchQueries: ["gorakhpur court case status", "gorakhpur district court case status", "district court gorakhpur case status"],
    smartSearchHint: "Gorakhpur District Court Uttar Pradesh",
    aboutCourt:
      "The District & Sessions Court at Gorakhpur is the principal court of the district judiciary in Gorakhpur, eastern Uttar Pradesh, headed by the District & Sessions Judge and supported by Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates. Alongside the main complex it includes a Commercial Court, a Motor Accident Claims Tribunal and an outlying court at Bansgaon. It hears civil suits, sessions trials, bail applications, matrimonial matters, motor-accident claims and cheque-bounce complaints under Section 138 of the Negotiable Instruments Act. Appeals and revisions from these courts lie to the Allahabad High Court. Every matter carries a CNR (Case Number Record) number, which is the most reliable way to track it online.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
      "Commercial Suits",
    ],
    commonProblems:
      "The eCourts portal shows Gorakhpur case status, but you have to look your matter up yourself and nothing tells you when the next date changes or an order is uploaded. India Case Status monitors your Gorakhpur matters continuously against the official record and alerts you and your team on WhatsApp and email, with the order attached when one is published.",
    extraFaqs: [
      { question: "How do I check Gorakhpur district court case status by CNR number?",
        answer: "Enter your 16-character CNR number on India Case Status. The platform locates the matter on the official record and then tracks it automatically, alerting you when the status, next date or orders change." },
      { question: "Which High Court do appeals from Gorakhpur go to?",
        answer: "Appeals and revisions from the Gorakhpur district judiciary lie to the Allahabad High Court at Prayagraj. You can track both your Gorakhpur district court and Allahabad High Court matters in one dashboard." },
      { question: "Can I track a case from the outlying court at Bansgaon?",
        answer: "Yes. Matters from the Bansgaon court and the other courts in Gorakhpur district can be tracked the same way — by CNR, case number or party name — with hearing and order alerts." },
      { question: "How do I find the next hearing date for my Gorakhpur case?",
        answer: "Add your case and the next date appears on your dashboard and hearing calendar, which you can export to Google or Outlook. You get an alert when a new date is published, so there is no need to check the portal daily." },
    ],
  },
  {
    slug: "bareilly-court-case-tracking",
    cityName: "Bareilly",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Bareilly District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Bareilly and the tehsil courts across the district — in 10 Indian languages.",
    highlights: [
      "District & Sessions Court, Bareilly",
      "Commercial Court, Bareilly",
      "Tehsil courts at Baheri, Aonla & Nawabganj",
      "Railway Court, Bareilly",
    ],
    courtInfo: {
      name: "Bareilly District Courts",
      jurisdiction: "Bareilly District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Bareilly Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Bareilly district court case status online by CNR, case number or party name. Track the District & Sessions Court, commercial court and tehsil courts with WhatsApp & email alerts.",
    seoH1: "Bareilly District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Bareilly — case tracking",
    altNames: ["Bareilly Sessions Court Case Status", "Bareilly District Court CNR Status"],
    searchQueries: ["bareilly court case status", "bareilly district court case status", "district court bareilly case status"],
    smartSearchHint: "Bareilly District Court Uttar Pradesh",
    aboutCourt:
      "The District & Sessions Court at Bareilly serves the Bareilly district in the Rohilkhand region of Uttar Pradesh. Headed by the District & Sessions Judge, it works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with tehsil-level courts at Baheri, Aonla and Nawabganj and a dedicated Commercial Court. The courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce complaints. Appeals and revisions lie to the Allahabad High Court. Each case has a CNR number that can be used to track it online.",
    caseTypes: [
      "Civil Suits & Property Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
      "Commercial Suits",
    ],
    commonProblems:
      "Checking Bareilly case status on the official portal means searching manually every day, and it never tells you when a new date is fixed or an order is uploaded. India Case Status watches your Bareilly matters continuously against the official record and alerts you on WhatsApp and email the moment something changes.",
    extraFaqs: [
      { question: "How do I check Bareilly court case status online?",
        answer: "Enter your CNR number, case number or the party name on India Case Status. Once the matter is matched on the official record it is tracked automatically, with alerts when the status, next date or orders change." },
      { question: "Which High Court hears appeals from Bareilly courts?",
        answer: "Appeals and revisions from the Bareilly district judiciary lie to the Allahabad High Court. Both can be tracked together in one dashboard." },
      { question: "Can I track a case filed at the Baheri or Aonla tehsil court?",
        answer: "Yes. Matters from the tehsil courts at Baheri, Aonla and Nawabganj are tracked the same way as the main Bareilly complex, with the same hearing and order alerts." },
      { question: "Do I get the order copy when one is uploaded?",
        answer: "When an order or judgment is published on the official record for your Bareilly case, you receive an alert and the order is available to view and download from your dashboard." },
    ],
  },
  {
    slug: "aligarh-court-case-tracking",
    cityName: "Aligarh",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Aligarh District Court cases and get instant alerts for hearing dates, status changes and new orders from the District & Sessions Court, Aligarh and its tehsil courts — on WhatsApp and email.",
    highlights: [
      "District & Sessions Court, Aligarh",
      "Commercial Court, Aligarh",
      "Tehsil courts at Atrauli, Khair & Iglas",
      "Gram Nyayalaya, Gabhana",
    ],
    courtInfo: {
      name: "Aligarh District Courts",
      jurisdiction: "Aligarh District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Aligarh Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Aligarh district court case status online by CNR, case number or party name. Track the District & Sessions Court, commercial court and tehsil courts with WhatsApp & email alerts.",
    seoH1: "Aligarh District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Aligarh — case tracking",
    altNames: ["Aligarh Sessions Court Case Status", "Aligarh District Court CNR Status"],
    searchQueries: ["aligarh court case status", "aligarh district court case status", "district court aligarh case status"],
    smartSearchHint: "Aligarh District Court Uttar Pradesh",
    aboutCourt:
      "The District & Sessions Court at Aligarh is the principal court of the district judiciary in Aligarh, western Uttar Pradesh. Led by the District & Sessions Judge with Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, the district also has tehsil courts at Atrauli, Khair and Iglas, a Commercial Court and a Gram Nyayalaya at Gabhana. These courts handle civil suits, sessions trials, bail applications, matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Allahabad High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
      "Commercial Suits",
    ],
    commonProblems:
      "The official portal shows Aligarh case status only if you search for it yourself, and it never warns you when the next date changes or an order is uploaded. India Case Status monitors your Aligarh matters continuously and alerts you and your clients on WhatsApp and email when something changes.",
    extraFaqs: [
      { question: "How do I check Aligarh district court case status by CNR?",
        answer: "Enter your 16-character CNR number on India Case Status. The matter is located on the official record and then tracked automatically, with alerts when the status, next date or orders change." },
      { question: "Which High Court do Aligarh appeals go to?",
        answer: "Appeals and revisions from the Aligarh district judiciary lie to the Allahabad High Court. Both your district court and High Court matters can be tracked in one place." },
      { question: "Can I track a Gram Nyayalaya matter from Gabhana?",
        answer: "Yes. Matters from the Gram Nyayalaya at Gabhana and the tehsil courts at Atrauli, Khair and Iglas are tracked the same way, with the same hearing and order alerts." },
      { question: "Can my client also receive the Aligarh case updates?",
        answer: "Yes. Every alert is client-forward-ready, and you can share a case so your client sees the next hearing date and status without needing an account." },
    ],
  },
  {
    slug: "moradabad-court-case-tracking",
    cityName: "Moradabad",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Moradabad District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Moradabad and the courts under it, in your language.",
    highlights: [
      "District & Sessions Court, Moradabad",
      "Motor Accident Claims Tribunal (MACT), Moradabad",
      "Railway Court, Moradabad",
      "Outlying court at Thakurdwara",
    ],
    courtInfo: {
      name: "Moradabad District Courts",
      jurisdiction: "Moradabad District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Moradabad Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Moradabad district court case status online by CNR, case number or party name. Track the District & Sessions Court, MACT and railway court with WhatsApp & email alerts.",
    seoH1: "Moradabad District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Moradabad — case tracking",
    altNames: ["Moradabad Sessions Court Case Status", "Moradabad District Court CNR Status"],
    searchQueries: ["moradabad court case status", "moradabad district court case status", "district court moradabad case status"],
    smartSearchHint: "Moradabad District Court Uttar Pradesh",
    aboutCourt:
      "The District & Sessions Court at Moradabad serves the Moradabad district in western Uttar Pradesh, known for its brassware trade. Headed by the District & Sessions Judge with Additional District Judges, Civil Judges and Judicial Magistrates, it also includes a Motor Accident Claims Tribunal, a Railway Court and an outlying court at Thakurdwara. The courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce complaints. Appeals and revisions lie to the Allahabad High Court. Each case has a CNR number for online tracking.",
    caseTypes: [
      "Civil Suits & Property Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
      "Rent & Eviction Matters",
    ],
    commonProblems:
      "Looking up Moradabad case status on the official portal is a manual daily chore, and nothing tells you when a hearing date moves or an order is uploaded. India Case Status watches your Moradabad matters continuously and alerts you on WhatsApp and email the moment the record changes.",
    extraFaqs: [
      { question: "How do I check Moradabad court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Moradabad?",
        answer: "Appeals and revisions from the Moradabad district judiciary lie to the Allahabad High Court, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Railway Court at Moradabad?",
        answer: "Yes. Matters from the Railway Court and the outlying court at Thakurdwara are tracked the same way as the main complex, with the same alerts." },
      { question: "How will I know when my next Moradabad hearing is?",
        answer: "Your next hearing date appears on your dashboard and hearing calendar, and you receive an alert when a new date is published on the official record." },
    ],
  },
  {
    slug: "saharanpur-court-case-tracking",
    cityName: "Saharanpur",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Saharanpur District Court cases and never miss a hearing. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Saharanpur on WhatsApp and email.",
    highlights: [
      "District & Sessions Court, Saharanpur",
      "Tehsil court at Deoband",
      "Family Court, Saharanpur",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Saharanpur District Courts",
      jurisdiction: "Saharanpur District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Saharanpur Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Saharanpur district court case status online by CNR, case number or party name. Track the District & Sessions Court and Deoband tehsil court with WhatsApp & email alerts.",
    seoH1: "Saharanpur District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Saharanpur — case tracking",
    altNames: ["Saharanpur Sessions Court Case Status", "Saharanpur District Court CNR Status"],
    searchQueries: ["saharanpur court case status", "saharanpur district court case status", "district court saharanpur case status"],
    smartSearchHint: "Saharanpur District Court Uttar Pradesh",
    aboutCourt:
      "The District & Sessions Court at Saharanpur is the principal court of the district judiciary in Saharanpur, in the far north-west of Uttar Pradesh near the Uttarakhand border. It is headed by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with a tehsil court at Deoband. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Allahabad High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Saharanpur case status only on a manual search, and it never alerts you when the next date changes or an order is uploaded. India Case Status monitors your Saharanpur matters continuously and messages you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Saharanpur district court case status?",
        answer: "Enter your CNR number, case number or party name on India Case Status. The matter is located on the official record and then tracked automatically, with alerts on every change." },
      { question: "Which High Court do Saharanpur appeals go to?",
        answer: "Appeals and revisions from the Saharanpur district judiciary lie to the Allahabad High Court, and both can be tracked together." },
      { question: "Can I track a case filed at the Deoband tehsil court?",
        answer: "Yes. Matters from the Deoband court are tracked the same way as the main Saharanpur complex, with the same hearing and order alerts." },
      { question: "Is party-name search available for Saharanpur cases?",
        answer: "Yes. If you only know the petitioner or respondent name and the filing year, search by party name; once matched, the matter is tracked with full alerts." },
    ],
  },
  {
    slug: "jhansi-court-case-tracking",
    cityName: "Jhansi",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Jhansi District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Jhansi and the tehsil courts in Bundelkhand — in your language.",
    highlights: [
      "District & Sessions Court, Jhansi",
      "Commercial Court, Jhansi",
      "Tehsil courts at Garautha, Mauranipur & Moth",
      "Railway Court, Jhansi",
    ],
    courtInfo: {
      name: "Jhansi District Courts",
      jurisdiction: "Jhansi District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Jhansi Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Jhansi district court case status online by CNR, case number or party name. Track the District & Sessions Court, commercial court and tehsil courts with WhatsApp & email alerts.",
    seoH1: "Jhansi District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Jhansi — case tracking",
    altNames: ["Jhansi Sessions Court Case Status", "Jhansi District Court CNR Status"],
    searchQueries: ["jhansi court case status", "jhansi district court case status", "district court jhansi case status"],
    smartSearchHint: "Jhansi District Court Uttar Pradesh Bundelkhand",
    aboutCourt:
      "The District & Sessions Court at Jhansi is the principal court of the district judiciary in the Bundelkhand region of Uttar Pradesh. Headed by the District & Sessions Judge with Additional District Judges, Civil Judges and Judicial Magistrates, the district also has tehsil courts at Garautha, Mauranipur and Moth, a Commercial Court and a Railway Court. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Allahabad High Court. Each case has a CNR number for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
      "Commercial Suits",
    ],
    commonProblems:
      "Tracking a Jhansi case on the official portal means searching by hand each day, and it gives no warning when a date moves or an order is uploaded. India Case Status monitors your Jhansi matters continuously and alerts you on WhatsApp and email as soon as the record changes.",
    extraFaqs: [
      { question: "How do I check Jhansi court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Jhansi?",
        answer: "Appeals and revisions from the Jhansi district judiciary lie to the Allahabad High Court, and both can be tracked in one dashboard." },
      { question: "Can I track cases from the Garautha or Mauranipur tehsil courts?",
        answer: "Yes. Matters from the tehsil courts at Garautha, Mauranipur and Moth are tracked the same way as the main Jhansi complex, with the same alerts." },
      { question: "Does the platform cover commercial suits filed at Jhansi?",
        answer: "Yes. Matters before the Commercial Court at Jhansi are tracked like any other, with hearing and order alerts on WhatsApp and email." },
    ],
  },
  {
    slug: "mathura-court-case-tracking",
    cityName: "Mathura",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Mathura District Court cases and get instant alerts for hearing dates, status changes and new orders from the District & Sessions Court, Mathura and its courts — on WhatsApp and email in your language.",
    highlights: [
      "District & Sessions Court, Mathura",
      "Tehsil court at Chhata",
      "Railway Court, Mathura",
      "Family Court, Mathura",
    ],
    courtInfo: {
      name: "Mathura District Courts",
      jurisdiction: "Mathura District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Mathura Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Mathura district court case status online by CNR, case number or party name. Track the District & Sessions Court and Chhata tehsil court with WhatsApp & email alerts.",
    seoH1: "Mathura District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Mathura — case tracking",
    altNames: ["Mathura Sessions Court Case Status", "Mathura District Court CNR Status"],
    searchQueries: ["mathura court case status", "mathura district court case status", "district court mathura case status"],
    smartSearchHint: "Mathura District Court Uttar Pradesh",
    aboutCourt:
      "The District & Sessions Court at Mathura serves the Mathura district in the Braj region of western Uttar Pradesh. It is headed by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with a tehsil court at Chhata and a Railway Court. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Allahabad High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Mathura case status only when you search for it, and never tells you when the next date changes or an order is uploaded. India Case Status monitors your Mathura matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Mathura district court case status by CNR?",
        answer: "Enter your 16-character CNR number on India Case Status. The matter is located on the official record and then tracked automatically, with alerts on any change to status, next date or orders." },
      { question: "Which High Court do Mathura appeals go to?",
        answer: "Appeals and revisions from the Mathura district judiciary lie to the Allahabad High Court, and both can be tracked together in one dashboard." },
      { question: "Can I track a case filed at the Chhata tehsil court?",
        answer: "Yes. Matters from the Chhata court are tracked the same way as the main Mathura complex, with the same hearing and order alerts." },
      { question: "Can I add several Mathura cases at once?",
        answer: "Yes. You can add and track multiple matters together, each with its own hearing calendar and alerts, in a single dashboard." },
    ],
  },
  {
    slug: "ayodhya-court-case-tracking",
    cityName: "Ayodhya",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Ayodhya (Faizabad) District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Ayodhya and the courts under it.",
    highlights: [
      "District & Sessions Court, Ayodhya (Faizabad)",
      "Commercial Court, Ayodhya",
      "Motor Accident Claims Tribunal (MACT), Ayodhya",
      "Family Court, Ayodhya",
    ],
    courtInfo: {
      name: "Ayodhya District Courts",
      jurisdiction: "Ayodhya (Faizabad) District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Ayodhya (Faizabad) Court Case Status | District & Sessions Court Tracking",
    seoDescription:
      "Check Ayodhya (Faizabad) district court case status online by CNR, case number or party name. Track the District & Sessions Court with WhatsApp & email alerts.",
    seoH1: "Ayodhya District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Ayodhya (Faizabad) — case tracking",
    altNames: ["Faizabad Court Case Status", "Ayodhya Sessions Court Case Status", "Ayodhya District Court CNR Status"],
    searchQueries: ["ayodhya court case status", "faizabad court case status", "ayodhya district court case status"],
    smartSearchHint: "Ayodhya Faizabad District Court Uttar Pradesh",
    aboutCourt:
      "The District & Sessions Court at Ayodhya, historically known as Faizabad, is the principal court of the district judiciary for the Ayodhya district in central Uttar Pradesh. Headed by the District & Sessions Judge with Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, it also includes a Commercial Court and a Motor Accident Claims Tribunal. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Because Ayodhya falls in the Oudh region, appeals and revisions lie to the Lucknow Bench of the Allahabad High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
      "Commercial Suits",
    ],
    commonProblems:
      "Checking an Ayodhya or Faizabad case on the official portal means a manual search each day, with no warning when the date moves or an order is uploaded. India Case Status monitors your Ayodhya matters continuously and alerts you on WhatsApp and email as soon as the record changes.",
    extraFaqs: [
      { question: "Is Ayodhya district court the same as Faizabad court?",
        answer: "Yes. Faizabad district was renamed Ayodhya, and the district judiciary is the same. You can search by either name; matters are tracked by their CNR number regardless of the label." },
      { question: "Which High Court do appeals from Ayodhya go to?",
        answer: "Ayodhya falls in the Oudh region, so appeals and revisions lie to the Lucknow Bench of the Allahabad High Court. You can track both your district court and High Court matters together." },
      { question: "How do I check Ayodhya case status by CNR number?",
        answer: "Enter your 16-character CNR number on India Case Status. The matter is located on the official record and then tracked automatically, with alerts on any change." },
      { question: "Can my client receive the Ayodhya case updates too?",
        answer: "Yes. Alerts are client-forward-ready and you can share a case so your client sees the next date and status without an account." },
    ],
  },
  {
    slug: "muzaffarnagar-court-case-tracking",
    cityName: "Muzaffarnagar",
    stateName: "Uttar Pradesh",
    courtType: "District Court",
    description:
      "Track your Muzaffarnagar District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Muzaffarnagar and the tehsil court at Budhana.",
    highlights: [
      "District & Sessions Court, Muzaffarnagar",
      "Tehsil court at Budhana",
      "Family Court, Muzaffarnagar",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Muzaffarnagar District Courts",
      jurisdiction: "Muzaffarnagar District, Uttar Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Muzaffarnagar Court Case Status | District & Sessions Court Tracking",
    seoDescription:
      "Check Muzaffarnagar district court case status online by CNR, case number or party name. Track the District & Sessions Court and Budhana tehsil court with WhatsApp & email alerts.",
    seoH1: "Muzaffarnagar District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Muzaffarnagar — case tracking",
    altNames: ["Muzaffarnagar Sessions Court Case Status", "Muzaffarnagar District Court CNR Status"],
    searchQueries: ["muzaffarnagar court case status", "muzaffarnagar district court case status", "district court muzaffarnagar case status"],
    smartSearchHint: "Muzaffarnagar District Court Uttar Pradesh",
    aboutCourt:
      "The District & Sessions Court at Muzaffarnagar serves the Muzaffarnagar district in the sugarcane belt of western Uttar Pradesh. It is headed by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with a tehsil court at Budhana. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Allahabad High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Muzaffarnagar case status only on a manual search, and never alerts you when the next date changes or an order is uploaded. India Case Status monitors your Muzaffarnagar matters continuously and messages you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Muzaffarnagar court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Muzaffarnagar?",
        answer: "Appeals and revisions from the Muzaffarnagar district judiciary lie to the Allahabad High Court, and both can be tracked in one dashboard." },
      { question: "Can I track a case filed at the Budhana tehsil court?",
        answer: "Yes. Matters from the Budhana court are tracked the same way as the main Muzaffarnagar complex, with the same hearing and order alerts." },
      { question: "How do I find the next hearing date of my case?",
        answer: "Your next hearing date appears on your dashboard and hearing calendar, and you receive an alert when a new date is published on the official record." },
    ],
  },
  {
    slug: "yamunanagar-court-case-tracking",
    cityName: "Yamunanagar",
    stateName: "Haryana",
    courtType: "District Court",
    description:
      "Track your Yamunanagar District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District Court, Yamunanagar and the judicial complex at Bilaspur.",
    highlights: [
      "District Court, Yamunanagar",
      "Judicial Complex, Bilaspur (Yamunanagar)",
      "Family Court, Yamunanagar",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Yamunanagar District Courts",
      jurisdiction: "Yamunanagar District, Haryana",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Yamunanagar Court Case Status by CNR & Case Number",
    seoDescription:
      "Check Yamunanagar court case status by CNR, case number or party name. Follow official hearing dates, status changes and orders from Yamunanagar and Bilaspur courts.",
    seoH1: "Yamunanagar District Court Case Status",
    seoH1Subtitle: "District Court, Yamunanagar — case tracking",
    altNames: ["Yamuna Nagar Court Case Status", "Yamunanagar District Court CNR Status"],
    searchQueries: ["yamunanagar court case status", "yamuna nagar district court case status", "district court yamunanagar case status"],
    smartSearchHint: "Yamunanagar Yamuna Nagar District Court Haryana",
    aboutCourt:
      "The District Court at Yamunanagar is the principal court of the district judiciary in Yamunanagar, a district in northern Haryana. It is headed by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with a judicial complex at Bilaspur within the district. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Punjab and Haryana High Court at Chandigarh. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Property Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Yamunanagar case status only on a manual search, and never warns you when the next date changes or an order is uploaded. India Case Status monitors your Yamunanagar matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Yamunanagar court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Yamunanagar?",
        answer: "Appeals and revisions from the Yamunanagar district judiciary lie to the Punjab and Haryana High Court at Chandigarh, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Bilaspur judicial complex?",
        answer: "Yes. Matters from the Bilaspur complex within Yamunanagar district are tracked the same way as the main District Court, with the same alerts." },
      { question: "Is party-name search available for Yamunanagar cases?",
        answer: "Yes. If you only know the petitioner or respondent name and the filing year, search by party name; once matched, the matter is tracked with full alerts." },
    ],
  },
  {
    slug: "nellore-court-case-tracking",
    cityName: "Nellore",
    stateName: "Andhra Pradesh",
    courtType: "District Court",
    description:
      "Track your Nellore District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the Principal District & Sessions Court, Nellore and the taluka courts across the district.",
    highlights: [
      "Principal District & Sessions Court, Nellore",
      "Additional District Court, Gudur",
      "Taluka courts at Kavali, Kovur & Venkatagiri",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Nellore District Courts",
      jurisdiction: "SPSR Nellore District, Andhra Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Nellore Court Case Status by CNR, Case Number & Party Name",
    seoDescription:
      "Check Nellore court case status by CNR, case number or party name. Follow official hearing dates, status changes and orders from the district and taluka courts.",
    seoH1: "Nellore District Court Case Status",
    seoH1Subtitle: "Principal District & Sessions Court, Nellore — case tracking",
    altNames: ["SPSR Nellore Court Case Status", "Nellore District Court CNR Status"],
    searchQueries: ["nellore court case status", "nellore district court case status", "district court nellore case status"],
    smartSearchHint: "Nellore SPSR Nellore District Court Andhra Pradesh",
    aboutCourt:
      "The Principal District & Sessions Court at Nellore is the head of the district judiciary for the SPSR Nellore district in Andhra Pradesh. It works through Additional District & Sessions Judges, Senior and Junior Civil Judges and Judicial Magistrates, with courts spread across the district at Gudur, Kavali, Kovur, Venkatagiri, Atmakur, Sullurpet and other taluka centres. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Andhra Pradesh High Court at Amaravati. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The eCourts portal shows Nellore case status only when you search for it, and gives no warning when a date moves or an order is uploaded. India Case Status monitors your Nellore matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Nellore district court case status by CNR?",
        answer: "Enter your 16-character CNR number on India Case Status. The matter is located on the official record and then tracked automatically, with alerts on any change." },
      { question: "Which High Court hears appeals from Nellore?",
        answer: "Appeals and revisions from the Nellore district judiciary lie to the Andhra Pradesh High Court at Amaravati, and both can be tracked in one dashboard." },
      { question: "Can I track a case from a taluka court like Kavali or Gudur?",
        answer: "Yes. Matters from the taluka courts at Kavali, Gudur, Kovur, Venkatagiri and the other centres in Nellore district are tracked the same way, with the same alerts." },
      { question: "Are alerts available in Telugu?",
        answer: "Yes. Alerts can be delivered in Telugu and other Indian languages on WhatsApp and email." },
    ],
  },
  {
    slug: "alipore-court-case-status",
    cityName: "Kolkata",
    stateName: "West Bengal",
    courtType: "District Court",
    description:
      "Track your Alipore court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the Alipore Civil and Criminal Court complexes and the other courts of South 24 Parganas district.",
    highlights: [
      "Alipore Civil Court Complex",
      "Alipore Criminal Court Complex",
      "Commercial Court, Alipore",
      "Baruipur, Diamond Harbour & Sealdah complexes",
    ],
    courtInfo: {
      name: "Alipore Courts (South 24 Parganas)",
      jurisdiction: "South 24 Parganas District, West Bengal",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Alipore Court Case Status | South 24 Parganas Court Case Tracking",
    seoDescription:
      "Check Alipore court case status online by CNR, case number or party name. Track the Alipore civil and criminal courts and the South 24 Parganas district courts with WhatsApp & email alerts.",
    seoH1: "Alipore Court Case Status",
    seoH1Subtitle: "Alipore & South 24 Parganas courts — case tracking",
    altNames: ["Alipur Court Case Status", "South 24 Parganas Court Case Status", "Alipore Judges Court Status"],
    searchQueries: ["alipore court case status", "alipur court case status", "south 24 parganas court case status"],
    smartSearchHint: "Alipore Alipur South 24 Parganas Court Kolkata West Bengal",
    aboutCourt:
      "The Alipore courts form the district judiciary headquarters of the South 24 Parganas district in Kolkata, West Bengal. The Alipore Civil Court Complex and Alipore Criminal Court Complex house the District & Sessions Judge, Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, and the district also has a Commercial Court and court complexes at Baruipur, Diamond Harbour, Sealdah and Kakdwip. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Calcutta High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
      "Commercial Suits",
    ],
    commonProblems:
      "Checking an Alipore matter on the official portal means searching by hand each day across the civil and criminal complexes, with no warning when a date moves or an order is uploaded. India Case Status monitors your Alipore and South 24 Parganas matters continuously and alerts you on WhatsApp and email the moment the record changes.",
    extraFaqs: [
      { question: "Which district do the Alipore courts belong to?",
        answer: "The Alipore courts are the headquarters of the South 24 Parganas district judiciary. Matters from Alipore and the other complexes in the district are tracked by their CNR number." },
      { question: "Which High Court hears appeals from Alipore courts?",
        answer: "Appeals and revisions from the Alipore and South 24 Parganas courts lie to the Calcutta High Court, and both can be tracked in one dashboard." },
      { question: "Can I track both the Alipore civil and criminal court matters?",
        answer: "Yes. Matters from the Alipore Civil Court Complex, the Alipore Criminal Court Complex and the outlying complexes at Baruipur, Diamond Harbour and Sealdah are all tracked the same way." },
      { question: "How do I check an Alipore case by CNR number?",
        answer: "Enter your 16-character CNR number on India Case Status. The matter is located on the official record and then tracked automatically, with alerts on any change." },
    ],
  },
  {
    slug: "jabalpur-court-case-tracking",
    cityName: "Jabalpur",
    stateName: "Madhya Pradesh",
    courtType: "District Court",
    description:
      "Track your Jabalpur District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Jabalpur and the civil courts under it.",
    highlights: [
      "District & Sessions Court, Jabalpur",
      "Civil Court, Sihora",
      "Civil Court, Patan",
      "Family Court, Jabalpur",
    ],
    courtInfo: {
      name: "Jabalpur District Courts",
      jurisdiction: "Jabalpur District, Madhya Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Jabalpur District Court Case Status | District & Sessions Court",
    seoDescription:
      "Check Jabalpur district court case status online by CNR, case number or party name. Track the District & Sessions Court and the Sihora and Patan civil courts with WhatsApp & email alerts.",
    seoH1: "Jabalpur District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Jabalpur — case tracking",
    altNames: ["Jabalpur Sessions Court Case Status", "Jabalpur District Court CNR Status"],
    searchQueries: ["jabalpur court case status", "jabalpur district court case status", "district court jabalpur case status"],
    smartSearchHint: "Jabalpur District Court Madhya Pradesh",
    aboutCourt:
      "The District & Sessions Court at Jabalpur is the head of the district judiciary in Jabalpur, Madhya Pradesh. Led by the District & Sessions Judge with Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, the district also has civil courts at Sihora and Patan. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Jabalpur is also the seat of the Madhya Pradesh High Court, to which appeals and revisions from the district courts lie. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Jabalpur case status only on a manual search, and never alerts you when the next date changes or an order is uploaded. India Case Status monitors your Jabalpur matters continuously and messages you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Jabalpur district court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Jabalpur?",
        answer: "Jabalpur is the seat of the Madhya Pradesh High Court, so appeals and revisions from the district courts lie there. You can track both your district court and High Court matters together." },
      { question: "Can I track a case from the Sihora or Patan civil court?",
        answer: "Yes. Matters from the civil courts at Sihora and Patan are tracked the same way as the main Jabalpur complex, with the same hearing and order alerts." },
      { question: "Do I get the order copy when one is uploaded?",
        answer: "When an order or judgment is published on the official record for your Jabalpur case, you receive an alert and can view and download the order from your dashboard." },
    ],
  },
  {
    slug: "ranchi-district-court-case-tracking",
    cityName: "Ranchi",
    stateName: "Jharkhand",
    courtType: "District Court",
    description:
      "Track your Ranchi District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the Civil Court and District & Sessions Court at Ranchi.",
    highlights: [
      "District & Sessions Court, Ranchi",
      "Civil Court, Ranchi",
      "Family Court, Ranchi",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Ranchi District Courts",
      jurisdiction: "Ranchi District, Jharkhand",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Ranchi Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Ranchi district court case status online by CNR, case number or party name. Track the Civil Court and District & Sessions Court at Ranchi with WhatsApp & email alerts.",
    seoH1: "Ranchi District Court Case Status",
    seoH1Subtitle: "Civil Court & District Court, Ranchi — case tracking",
    altNames: ["Ranchi Civil Court Case Status", "Ranchi District Court CNR Status"],
    searchQueries: ["ranchi court case status", "ranchi district court case status", "ranchi civil court case status"],
    smartSearchHint: "Ranchi District Court Civil Court Jharkhand",
    aboutCourt:
      "The District & Sessions Court at Ranchi, housed in the Ranchi Civil Court, is the head of the district judiciary in Ranchi, the capital of Jharkhand. It is led by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Ranchi is also the seat of the Jharkhand High Court, to which appeals and revisions from the district courts lie. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "Looking up a Ranchi case on the official portal is a manual daily task, and it never tells you when a hearing date moves or an order is uploaded. India Case Status watches your Ranchi matters continuously and alerts you on WhatsApp and email the moment the record changes.",
    extraFaqs: [
      { question: "How do I check Ranchi civil court case status by CNR?",
        answer: "Enter your 16-character CNR number on India Case Status. The matter is located on the official record and then tracked automatically, with alerts on any change." },
      { question: "Which High Court hears appeals from Ranchi?",
        answer: "Ranchi is the seat of the Jharkhand High Court, so appeals and revisions from the district courts lie there. Both can be tracked in one dashboard." },
      { question: "Can I check a Ranchi case by party name?",
        answer: "Yes. Search by petitioner or respondent name and filing year; once matched, the matter is tracked continuously with hearing and order alerts." },
      { question: "How will I know when my next Ranchi hearing is?",
        answer: "Your next hearing date appears on your dashboard and hearing calendar, and you receive an alert when a new date is published on the official record." },
    ],
  },
  {
    slug: "guntur-court-case-tracking",
    cityName: "Guntur",
    stateName: "Andhra Pradesh",
    courtType: "District Court",
    description:
      "Track your Guntur District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the Principal District Court, Guntur and the courts across the district.",
    highlights: [
      "Principal District Court Complex, Guntur",
      "Additional District Court (FTC), Tenali",
      "Courts at Narasaraopet, Bapatla & Mangalagiri",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Guntur District Courts",
      jurisdiction: "Guntur District, Andhra Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Guntur Court Case Status by CNR, Case Number & Party Name",
    seoDescription:
      "Check Guntur court case status by CNR, case number or party name. Follow official hearing dates, status changes and orders from Guntur district courts.",
    seoH1: "Guntur District Court Case Status",
    seoH1Subtitle: "Principal District Court, Guntur — case tracking",
    altNames: ["Guntur Sessions Court Case Status", "Guntur District Court CNR Status"],
    searchQueries: ["guntur court case status", "guntur district court case status", "district court guntur case status"],
    smartSearchHint: "Guntur District Court Andhra Pradesh",
    aboutCourt:
      "The Principal District Court at Guntur heads the district judiciary for the Guntur district in Andhra Pradesh. It works through Additional District & Sessions Judges, Senior and Junior Civil Judges and Judicial Magistrates, with courts spread across the district at Tenali, Narasaraopet, Bapatla, Mangalagiri, Sattenapalli, Chilakaluripet and other centres. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Andhra Pradesh High Court at Amaravati. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The eCourts portal shows Guntur case status only when you search for it, and gives no warning when a date moves or an order is uploaded. India Case Status monitors your Guntur matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Guntur district court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Guntur?",
        answer: "Appeals and revisions from the Guntur district judiciary lie to the Andhra Pradesh High Court at Amaravati, and both can be tracked together." },
      { question: "Can I track a case from the Tenali or Narasaraopet courts?",
        answer: "Yes. Matters from the courts at Tenali, Narasaraopet, Bapatla, Mangalagiri and the other centres in Guntur district are tracked the same way, with the same alerts." },
      { question: "Are alerts available in Telugu?",
        answer: "Yes. Alerts can be delivered in Telugu and other Indian languages on WhatsApp and email." },
    ],
  },
  {
    slug: "tirupati-court-case-tracking",
    cityName: "Tirupati",
    stateName: "Andhra Pradesh",
    courtType: "District Court",
    description:
      "Track your Tirupati court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District Courts at Tirupati and the Chittoor judicial district.",
    highlights: [
      "III Additional District Court, Tirupati",
      "VI Junior Civil Court, Tirupati",
      "Court at Srikalahasti",
      "Principal District Court, Chittoor",
    ],
    courtInfo: {
      name: "Tirupati Courts (Chittoor District)",
      jurisdiction: "Tirupati, within the Chittoor judicial district, Andhra Pradesh",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Tirupati Court Case Status | District Court Case Tracking",
    seoDescription:
      "Check Tirupati court case status online by CNR, case number or party name. Track the Tirupati district courts within the Chittoor judicial district with WhatsApp & email alerts.",
    seoH1: "Tirupati Court Case Status",
    seoH1Subtitle: "Tirupati district courts — case tracking",
    altNames: ["Tirupati District Court Case Status", "Tirupati Sessions Court Status"],
    searchQueries: ["tirupati court case status", "tirupati district court case status", "district court tirupati case status"],
    smartSearchHint: "Tirupati Chittoor District Court Andhra Pradesh",
    aboutCourt:
      "The district courts at Tirupati serve the temple city of Tirupati in Andhra Pradesh. Although Tirupati became a separate revenue district in 2022, its courts continue to sit within the Chittoor judicial district for eCourts purposes, including the III Additional District Court and VI Junior Civil Court at Tirupati, courts at Srikalahasti, and the Principal District Court at Chittoor. They hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Andhra Pradesh High Court at Amaravati. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "Finding a Tirupati matter on the official portal means a manual search, and it never warns you when the next date changes or an order is uploaded. India Case Status monitors your Tirupati matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "Which judicial district do the Tirupati courts fall under?",
        answer: "For court records, Tirupati's courts sit within the Chittoor judicial district on eCourts, even though Tirupati is now a separate revenue district. Matters are tracked by their CNR number regardless." },
      { question: "Which High Court hears appeals from Tirupati?",
        answer: "Appeals and revisions from the Tirupati and Chittoor courts lie to the Andhra Pradesh High Court at Amaravati, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the court at Srikalahasti?",
        answer: "Yes. Matters from Srikalahasti and the other courts of the Chittoor judicial district are tracked the same way as the Tirupati courts, with the same alerts." },
      { question: "How do I check a Tirupati case by CNR number?",
        answer: "Enter your 16-character CNR number on India Case Status. The matter is located on the official record and then tracked automatically, with alerts on any change." },
    ],
  },
  {
    slug: "rohtak-court-case-tracking",
    cityName: "Rohtak",
    stateName: "Haryana",
    courtType: "District Court",
    description:
      "Track your Rohtak District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District Court, Rohtak and the judicial complex at Meham.",
    highlights: [
      "District Court, Rohtak",
      "Judicial Complex, Meham",
      "Family Court, Rohtak",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Rohtak District Courts",
      jurisdiction: "Rohtak District, Haryana",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Rohtak Court Case Status by CNR, Case Number & Party Name",
    seoDescription:
      "Check Rohtak court case status by CNR, case number or party name. Follow official hearing dates, status changes and orders from Rohtak and Meham courts.",
    seoH1: "Rohtak District Court Case Status",
    seoH1Subtitle: "District Court, Rohtak — case tracking",
    altNames: ["Rohtak Sessions Court Case Status", "Rohtak District Court CNR Status"],
    searchQueries: ["rohtak court case status", "rohtak district court case status", "district court rohtak case status"],
    smartSearchHint: "Rohtak District Court Haryana",
    aboutCourt:
      "The District Court at Rohtak is the principal court of the district judiciary in Rohtak, Haryana. It is headed by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with a judicial complex at Meham. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Punjab and Haryana High Court at Chandigarh. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Property Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Rohtak case status only on a manual search, and never alerts you when the next date changes or an order is uploaded. India Case Status monitors your Rohtak matters continuously and messages you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Rohtak court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Rohtak?",
        answer: "Appeals and revisions from the Rohtak district judiciary lie to the Punjab and Haryana High Court at Chandigarh, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Meham judicial complex?",
        answer: "Yes. Matters from the Meham complex are tracked the same way as the main Rohtak District Court, with the same hearing and order alerts." },
      { question: "How do I find the next hearing date of my Rohtak case?",
        answer: "Your next hearing date appears on your dashboard and hearing calendar, and you receive an alert when a new date is published on the official record." },
    ],
  },
  {
    slug: "hisar-court-case-tracking",
    cityName: "Hisar",
    stateName: "Haryana",
    courtType: "District Court",
    description:
      "Track your Hisar District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District Court, Hisar and the judicial complexes across the district.",
    highlights: [
      "District Court, Hisar",
      "Judicial Complex, Hansi",
      "Judicial complexes at Narnaund & Barwala",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Hisar District Courts",
      jurisdiction: "Hisar District, Haryana",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Hisar Court Case Status | District Court Case Tracking",
    seoDescription:
      "Check Hisar district court case status online by CNR, case number or party name. Track the District Court and the Hansi, Narnaund and Barwala complexes with WhatsApp & email alerts.",
    seoH1: "Hisar District Court Case Status",
    seoH1Subtitle: "District Court, Hisar — case tracking",
    altNames: ["Hisar Sessions Court Case Status", "Hissar Court Case Status", "Hisar District Court CNR Status"],
    searchQueries: ["hisar court case status", "hisar district court case status", "district court hisar case status"],
    smartSearchHint: "Hisar Hissar District Court Haryana",
    aboutCourt:
      "The District Court at Hisar is the principal court of the district judiciary in Hisar, Haryana. Headed by the District & Sessions Judge with Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, the district also has judicial complexes at Hansi, Narnaund and Barwala. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Punjab and Haryana High Court at Chandigarh. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Property Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "Checking a Hisar case on the official portal means searching by hand each day across the district's complexes, with no warning when a date moves or an order is uploaded. India Case Status monitors your Hisar matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Hisar court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Hisar?",
        answer: "Appeals and revisions from the Hisar district judiciary lie to the Punjab and Haryana High Court at Chandigarh, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Hansi or Barwala complex?",
        answer: "Yes. Matters from the judicial complexes at Hansi, Narnaund and Barwala are tracked the same way as the main Hisar District Court, with the same alerts." },
      { question: "Is party-name search available for Hisar cases?",
        answer: "Yes. If you only know the petitioner or respondent name and the filing year, search by party name; once matched, the matter is tracked with full alerts." },
    ],
  },
  {
    slug: "solapur-court-case-tracking",
    cityName: "Solapur",
    stateName: "Maharashtra",
    courtType: "District Court",
    description:
      "Track your Solapur District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Solapur and the taluka courts across the district.",
    highlights: [
      "District & Sessions Court, Solapur",
      "District & Sessions Court, Barshi",
      "Courts at Pandharpur, Akkalkot & Karmala",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Solapur District Courts",
      jurisdiction: "Solapur District, Maharashtra",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Solapur Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Solapur district court case status online by CNR, case number or party name. Track the District & Sessions Court and taluka courts with WhatsApp & email alerts.",
    seoH1: "Solapur District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Solapur — case tracking",
    altNames: ["Solapur Sessions Court Case Status", "Sholapur Court Case Status", "Solapur District Court CNR Status"],
    searchQueries: ["solapur court case status", "solapur district court case status", "district court solapur case status"],
    smartSearchHint: "Solapur Sholapur District Court Maharashtra",
    aboutCourt:
      "The District & Sessions Court at Solapur heads the district judiciary for the Solapur district in Maharashtra. It works through Additional District & Sessions Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with courts across the district at Barshi, Pandharpur, Akkalkot, Karmala, Madha, Malshiras, Mangalvedha, Mohol and Sangola. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Bombay High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Solapur case status only when you search for it, and never warns you when a date moves or an order is uploaded. India Case Status monitors your Solapur matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Solapur district court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Solapur?",
        answer: "Appeals and revisions from the Solapur district judiciary lie to the Bombay High Court, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Pandharpur or Barshi court?",
        answer: "Yes. Matters from the courts at Barshi, Pandharpur, Akkalkot and the other talukas in Solapur district are tracked the same way, with the same alerts." },
      { question: "Are alerts available in Marathi?",
        answer: "Yes. Alerts can be delivered in Marathi and other Indian languages on WhatsApp and email." },
    ],
  },
  {
    slug: "kolhapur-court-case-tracking",
    cityName: "Kolhapur",
    stateName: "Maharashtra",
    courtType: "District Court",
    description:
      "Track your Kolhapur District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Kolhapur and the taluka courts across the district.",
    highlights: [
      "District & Sessions Court, Kolhapur",
      "District & Sessions Court, Ichalkaranji",
      "Courts at Gadhinglaj, Jaysingpur & Kagal",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Kolhapur District Courts",
      jurisdiction: "Kolhapur District, Maharashtra",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Kolhapur Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Kolhapur district court case status online by CNR, case number or party name. Track the District & Sessions Court and taluka courts with WhatsApp & email alerts.",
    seoH1: "Kolhapur District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Kolhapur — case tracking",
    altNames: ["Kolhapur Sessions Court Case Status", "Kolhapur District Court CNR Status"],
    searchQueries: ["kolhapur court case status", "kolhapur district court case status", "district court kolhapur case status"],
    smartSearchHint: "Kolhapur District Court Maharashtra",
    aboutCourt:
      "The District & Sessions Court at Kolhapur heads the district judiciary for the Kolhapur district in southern Maharashtra. It works through Additional District & Sessions Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with courts across the district at Ichalkaranji, Gadhinglaj, Jaysingpur, Kagal, Panhala, Gargoti and other centres. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Bombay High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "Checking a Kolhapur case on the official portal means a manual search each day, with no warning when a date moves or an order is uploaded. India Case Status monitors your Kolhapur matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Kolhapur district court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Kolhapur?",
        answer: "Appeals and revisions from the Kolhapur district judiciary lie to the Bombay High Court, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Ichalkaranji or Gadhinglaj court?",
        answer: "Yes. Matters from the courts at Ichalkaranji, Gadhinglaj, Jaysingpur and the other talukas in Kolhapur district are tracked the same way, with the same alerts." },
      { question: "Are alerts available in Marathi?",
        answer: "Yes. Alerts can be delivered in Marathi and other Indian languages on WhatsApp and email." },
    ],
  },
  {
    slug: "aurangabad-district-court-case-tracking",
    cityName: "Aurangabad",
    stateName: "Maharashtra",
    courtType: "District Court",
    description:
      "Track your Aurangabad (Chhatrapati Sambhajinagar) District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court and the taluka courts across the district.",
    highlights: [
      "District & Sessions Court, Aurangabad",
      "Courts at Vaijapur, Paithan & Gangapur",
      "Courts at Sillod, Kannad & Phulambri",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Aurangabad District Courts (Chhatrapati Sambhajinagar)",
      jurisdiction: "Aurangabad (Chhatrapati Sambhajinagar) District, Maharashtra",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Aurangabad District Court Case Status | Chhatrapati Sambhajinagar Tracking",
    seoDescription:
      "Check Aurangabad (Chhatrapati Sambhajinagar) district court case status online by CNR, case number or party name. Track the District & Sessions Court and taluka courts with WhatsApp & email alerts.",
    seoH1: "Aurangabad District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Chhatrapati Sambhajinagar — case tracking",
    altNames: ["Chhatrapati Sambhajinagar Court Case Status", "Aurangabad Sessions Court Case Status", "Aurangabad Maharashtra District Court Status"],
    searchQueries: ["aurangabad district court case status", "chhatrapati sambhajinagar court case status", "aurangabad maharashtra court case status"],
    smartSearchHint: "Aurangabad Chhatrapati Sambhajinagar District Court Maharashtra",
    aboutCourt:
      "The District & Sessions Court at Aurangabad, now officially Chhatrapati Sambhajinagar, heads the district judiciary for the district in the Marathwada region of Maharashtra. It works through Additional District & Sessions Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with courts across the district at Vaijapur, Paithan, Gangapur, Sillod, Kannad, Khultabad, Soygaon and Phulambri. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Aurangabad is also home to a bench of the Bombay High Court, and appeals and revisions from the district courts lie to the Aurangabad Bench of the Bombay High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Aurangabad case status only when you search for it, and never warns you when a date moves or an order is uploaded. India Case Status monitors your Aurangabad matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "Is Aurangabad district court the same as Chhatrapati Sambhajinagar?",
        answer: "Yes. Aurangabad district was renamed Chhatrapati Sambhajinagar, and the district judiciary is the same. You can search by either name; matters are tracked by their CNR number." },
      { question: "Which High Court hears appeals from Aurangabad?",
        answer: "Aurangabad has a bench of the Bombay High Court, so appeals and revisions from the district courts lie to the Aurangabad Bench of the Bombay High Court. Both can be tracked in one dashboard." },
      { question: "Can I track a case from the Vaijapur or Paithan court?",
        answer: "Yes. Matters from the courts at Vaijapur, Paithan, Gangapur and the other talukas in the district are tracked the same way, with the same alerts." },
      { question: "How do I check an Aurangabad case by CNR number?",
        answer: "Enter your 16-character CNR number on India Case Status. The matter is located on the official record and then tracked automatically, with alerts on any change." },
    ],
  },
  {
    slug: "muzaffarpur-court-case-tracking",
    cityName: "Muzaffarpur",
    stateName: "Bihar",
    courtType: "District Court",
    description:
      "Track your Muzaffarpur District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the Muzaffarpur Civil Court and the courts under it.",
    highlights: [
      "District & Sessions Court, Muzaffarpur",
      "Muzaffarpur Civil Court",
      "Family Court, Muzaffarpur",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Muzaffarpur District Courts",
      jurisdiction: "Muzaffarpur District, Bihar",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Muzaffarpur Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Muzaffarpur district court case status online by CNR, case number or party name. Track the Muzaffarpur Civil Court and District & Sessions Court with WhatsApp & email alerts.",
    seoH1: "Muzaffarpur District Court Case Status",
    seoH1Subtitle: "Muzaffarpur Civil Court — case tracking",
    altNames: ["Muzaffarpur Civil Court Case Status", "Muzaffarpur District Court CNR Status"],
    searchQueries: ["muzaffarpur court case status", "muzaffarpur district court case status", "muzaffarpur civil court case status"],
    smartSearchHint: "Muzaffarpur District Court Bihar",
    aboutCourt:
      "The District & Sessions Court at Muzaffarpur, housed in the Muzaffarpur Civil Court, is the head of the district judiciary in Muzaffarpur, northern Bihar. It is led by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Patna High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Muzaffarpur case status only on a manual search, and never alerts you when the next date changes or an order is uploaded. India Case Status monitors your Muzaffarpur matters continuously and messages you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Muzaffarpur civil court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Muzaffarpur?",
        answer: "Appeals and revisions from the Muzaffarpur district judiciary lie to the Patna High Court, and both can be tracked in one dashboard." },
      { question: "Can I check a Muzaffarpur case by party name?",
        answer: "Yes. Search by petitioner or respondent name and filing year; once matched, the matter is tracked continuously with hearing and order alerts." },
      { question: "How will I know when my next Muzaffarpur hearing is?",
        answer: "Your next hearing date appears on your dashboard and hearing calendar, and you receive an alert when a new date is published on the official record." },
    ],
  },
  {
    slug: "jodhpur-district-court-case-tracking",
    cityName: "Jodhpur",
    stateName: "Rajasthan",
    courtType: "District Court",
    description:
      "Track your Jodhpur District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District & Sessions Court, Jodhpur and the courts across the district.",
    highlights: [
      "District & Sessions Court, Jodhpur",
      "Court complexes at Pipar City & Bilara",
      "Court complexes at Osian & Balesar",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Jodhpur District Courts",
      jurisdiction: "Jodhpur District, Rajasthan",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Jodhpur District Court Case Status | District & Sessions Court Tracking",
    seoDescription:
      "Check Jodhpur district court case status online by CNR, case number or party name. Track the District & Sessions Court and the Pipar City, Bilara and Osian complexes with WhatsApp & email alerts.",
    seoH1: "Jodhpur District Court Case Status",
    seoH1Subtitle: "District & Sessions Court, Jodhpur — case tracking",
    altNames: ["Jodhpur Sessions Court Case Status", "Jodhpur District Court CNR Status"],
    searchQueries: ["jodhpur district court case status", "jodhpur court case status", "district court jodhpur case status"],
    smartSearchHint: "Jodhpur District Court Rajasthan",
    aboutCourt:
      "The District & Sessions Court at Jodhpur heads the district judiciary for the Jodhpur district in Rajasthan. It works through Additional District & Sessions Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with court complexes across the district at Pipar City, Bilara, Osian, Balesar and Bhopalgarh. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Jodhpur is also the principal seat of the Rajasthan High Court, to which appeals and revisions from the district courts lie. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Jodhpur case status only when you search for it, and never warns you when a date moves or an order is uploaded. India Case Status monitors your Jodhpur matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Jodhpur district court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Jodhpur?",
        answer: "Jodhpur is the principal seat of the Rajasthan High Court, so appeals and revisions from the district courts lie there. Both can be tracked in one dashboard." },
      { question: "Can I track a case from the Pipar City or Bilara complex?",
        answer: "Yes. Matters from the court complexes at Pipar City, Bilara, Osian, Balesar and Bhopalgarh are tracked the same way as the main Jodhpur complex, with the same alerts." },
      { question: "Are alerts available in Hindi?",
        answer: "Yes. Alerts can be delivered in Hindi and other Indian languages on WhatsApp and email." },
    ],
  },
  {
    slug: "patiala-court-case-tracking",
    cityName: "Patiala",
    stateName: "Punjab",
    courtType: "District Court",
    description:
      "Track your Patiala District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District Court, Patiala and the judicial complexes across the district.",
    highlights: [
      "District Court, Patiala",
      "Judicial Complex, Rajpura",
      "Judicial complexes at Nabha & Samana",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Patiala District Courts",
      jurisdiction: "Patiala District, Punjab",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Patiala Court Case Status by CNR & Case Number",
    seoDescription:
      "Check Patiala court case status by CNR, case number or party name. Follow official hearings, status changes and orders from Patiala, Rajpura, Nabha and Samana courts.",
    seoH1: "Patiala District Court Case Status",
    seoH1Subtitle: "District Court, Patiala — case tracking",
    altNames: ["Patiala Sessions Court Case Status", "Patiala District Court CNR Status"],
    searchQueries: ["patiala court case status", "patiala district court case status", "district court patiala case status"],
    smartSearchHint: "Patiala District Court Punjab",
    aboutCourt:
      "The District Court at Patiala is the principal court of the district judiciary in Patiala, Punjab. It is headed by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with judicial complexes at Rajpura, Nabha and Samana. These courts hear civil suits, sessions trials, bail applications, family and matrimonial matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Punjab and Haryana High Court at Chandigarh. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Property Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Patiala case status only on a manual search, and never alerts you when the next date changes or an order is uploaded. India Case Status monitors your Patiala matters continuously and messages you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Patiala court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Patiala?",
        answer: "Appeals and revisions from the Patiala district judiciary lie to the Punjab and Haryana High Court at Chandigarh, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Rajpura or Nabha complex?",
        answer: "Yes. Matters from the judicial complexes at Rajpura, Nabha and Samana are tracked the same way as the main Patiala District Court, with the same alerts." },
      { question: "How do I find the next hearing date of my Patiala case?",
        answer: "Your next hearing date appears on your dashboard and hearing calendar, and you receive an alert when a new date is published on the official record." },
    ],
  },
  {
    slug: "mangaluru-court-case-tracking",
    cityName: "Mangaluru",
    stateName: "Karnataka",
    courtType: "District Court",
    description:
      "Track your Mangaluru (Dakshina Kannada) District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District Court and the taluka court complexes across the district.",
    highlights: [
      "District Court Complex, Dakshina Kannada",
      "Court complexes at Bantwal & Puttur",
      "Court complexes at Sullia & Mudbidri",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Mangaluru District Courts (Dakshina Kannada)",
      jurisdiction: "Dakshina Kannada District, Karnataka",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Mangaluru Court Case Status | Dakshina Kannada by CNR",
    seoDescription:
      "Check Mangaluru and Dakshina Kannada court case status by CNR, case number or party name. Follow official hearings, status changes and district court orders.",
    seoH1: "Mangaluru District Court Case Status",
    seoH1Subtitle: "Dakshina Kannada District Court — case tracking",
    altNames: ["Mangalore Court Case Status", "Dakshina Kannada Court Case Status", "Mangaluru District Court CNR Status"],
    searchQueries: ["mangaluru court case status", "mangalore court case status", "dakshina kannada district court case status"],
    smartSearchHint: "Mangaluru Mangalore Dakshina Kannada District Court Karnataka",
    aboutCourt:
      "The District Court at Mangaluru is the head of the district judiciary for the Dakshina Kannada district on the Karnataka coast. It works through Additional District & Sessions Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with court complexes across the district at Bantwal, Puttur, Sullia, Mudbidri and Belthangady. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Karnataka High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "Checking a Mangaluru case on the official portal means a manual search each day across the district's complexes, with no warning when a date moves or an order is uploaded. India Case Status monitors your Dakshina Kannada matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "Is Mangaluru court the same as Dakshina Kannada district court?",
        answer: "Yes. Mangaluru is the headquarters of the Dakshina Kannada district judiciary. Matters from Mangaluru and the taluka courts are tracked by their CNR number." },
      { question: "Which High Court hears appeals from Mangaluru?",
        answer: "Appeals and revisions from the Dakshina Kannada courts lie to the Karnataka High Court, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Bantwal or Puttur complex?",
        answer: "Yes. Matters from the court complexes at Bantwal, Puttur, Sullia, Mudbidri and Belthangady are tracked the same way as the main Mangaluru District Court, with the same alerts." },
      { question: "Are alerts available in Kannada?",
        answer: "Yes. Alerts can be delivered in Kannada and other Indian languages on WhatsApp and email." },
    ],
  },
  {
    slug: "ernakulam-court-case-tracking",
    cityName: "Ernakulam",
    stateName: "Kerala",
    courtType: "District Court",
    description:
      "Track your Ernakulam District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District Court, Ernakulam and the court complexes across the district.",
    highlights: [
      "District Court, Ernakulam",
      "Court complexes at Aluva & Muvattupuzha",
      "Court complexes at North Paravur & Kothamangalam",
      "Family Courts at Ernakulam & Aluva",
    ],
    courtInfo: {
      name: "Ernakulam District Courts",
      jurisdiction: "Ernakulam District, Kerala",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Ernakulam Court Case Status | District Court Case Tracking",
    seoDescription:
      "Check Ernakulam district court case status online by CNR, case number or party name. Track the District Court and the Aluva, Muvattupuzha and North Paravur complexes with WhatsApp & email alerts.",
    seoH1: "Ernakulam District Court Case Status",
    seoH1Subtitle: "District Court, Ernakulam — case tracking",
    altNames: ["Ernakulam Sessions Court Case Status", "Kochi District Court Case Status", "Ernakulam District Court CNR Status"],
    searchQueries: ["ernakulam court case status", "ernakulam district court case status", "district court ernakulam case status"],
    smartSearchHint: "Ernakulam Kochi District Court Kerala",
    aboutCourt:
      "The District Court at Ernakulam heads the district judiciary for the Ernakulam district in Kerala, which includes the city of Kochi. It works through Additional District & Sessions Judges, Sub Judges, Munsiff-Magistrates and Judicial First Class Magistrates, with court complexes across the district at Aluva, Muvattupuzha, North Paravur, Kothamangalam, Perumbavoor, Tripunithura, Kochi and Mattancherry, and Family Courts at Ernakulam and Aluva. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Ernakulam is also the seat of the Kerala High Court, to which appeals and revisions from the district courts lie. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Ernakulam case status only when you search for it, and never warns you when a date moves or an order is uploaded. India Case Status monitors your Ernakulam matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Ernakulam district court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Ernakulam?",
        answer: "Ernakulam is the seat of the Kerala High Court, so appeals and revisions from the district courts lie there. Both can be tracked in one dashboard." },
      { question: "Can I track a case from the Aluva or Muvattupuzha complex?",
        answer: "Yes. Matters from the court complexes at Aluva, Muvattupuzha, North Paravur, Kothamangalam and the others in Ernakulam district are tracked the same way, with the same alerts." },
      { question: "Are alerts available in Malayalam?",
        answer: "Yes. Alerts can be delivered in Malayalam and other Indian languages on WhatsApp and email." },
    ],
  },
  {
    slug: "kollam-court-case-tracking",
    cityName: "Kollam",
    stateName: "Kerala",
    courtType: "District Court",
    description:
      "Track your Kollam District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the District Court, Kollam and the court complexes across the district.",
    highlights: [
      "District Court, Kollam",
      "Motor Accident Claims Tribunal (MACT), Kollam",
      "Court complexes at Punalur & Karunagappally",
      "Court complexes at Kottarakkara & Paravur",
    ],
    courtInfo: {
      name: "Kollam District Courts",
      jurisdiction: "Kollam District, Kerala",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Kollam Court Case Status | District Court Case Tracking",
    seoDescription:
      "Check Kollam district court case status online by CNR, case number or party name. Track the District Court, MACT and the Punalur, Karunagappally and Kottarakkara complexes with WhatsApp & email alerts.",
    seoH1: "Kollam District Court Case Status",
    seoH1Subtitle: "District Court, Kollam — case tracking",
    altNames: ["Kollam Sessions Court Case Status", "Quilon Court Case Status", "Kollam District Court CNR Status"],
    searchQueries: ["kollam court case status", "kollam district court case status", "district court kollam case status"],
    smartSearchHint: "Kollam Quilon District Court Kerala",
    aboutCourt:
      "The District Court at Kollam heads the district judiciary for the Kollam district on the Kerala coast. It works through Additional District & Sessions Judges, Sub Judges, Munsiff-Magistrates and Judicial First Class Magistrates, with a Motor Accident Claims Tribunal and court complexes across the district at Punalur, Karunagappally, Kottarakkara, Paravur, Sasthamcotta and Pathanapuram. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Kerala High Court at Ernakulam. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "Checking a Kollam case on the official portal means a manual search each day, with no warning when a date moves or an order is uploaded. India Case Status monitors your Kollam matters continuously and alerts you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Kollam district court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Kollam?",
        answer: "Appeals and revisions from the Kollam district judiciary lie to the Kerala High Court at Ernakulam, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Punalur or Kottarakkara complex?",
        answer: "Yes. Matters from the court complexes at Punalur, Karunagappally, Kottarakkara and Paravur are tracked the same way as the main Kollam District Court, with the same alerts." },
      { question: "Are alerts available in Malayalam?",
        answer: "Yes. Alerts can be delivered in Malayalam and other Indian languages on WhatsApp and email." },
    ],
  },
  {
    slug: "bhagalpur-court-case-tracking",
    cityName: "Bhagalpur",
    stateName: "Bihar",
    courtType: "District Court",
    description:
      "Track your Bhagalpur District Court cases on WhatsApp and email. Get alerts for hearing dates, status changes and new orders from the Bhagalpur Civil Court and the courts under it.",
    highlights: [
      "District & Sessions Court, Bhagalpur",
      "Civil Court, Kahalgaon",
      "Civil Court Complex, Naugachia",
      "Motor Accident Claims Tribunal (MACT)",
    ],
    courtInfo: {
      name: "Bhagalpur District Courts",
      jurisdiction: "Bhagalpur District, Bihar",
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: "Bhagalpur Court Case Status | District & Sessions Court Case Tracking",
    seoDescription:
      "Check Bhagalpur district court case status online by CNR, case number or party name. Track the Bhagalpur Civil Court and the Kahalgaon and Naugachia courts with WhatsApp & email alerts.",
    seoH1: "Bhagalpur District Court Case Status",
    seoH1Subtitle: "Bhagalpur Civil Court — case tracking",
    altNames: ["Bhagalpur Civil Court Case Status", "Bhagalpur District Court CNR Status"],
    searchQueries: ["bhagalpur court case status", "bhagalpur district court case status", "bhagalpur civil court case status"],
    smartSearchHint: "Bhagalpur District Court Bihar",
    aboutCourt:
      "The District & Sessions Court at Bhagalpur, housed in the Bhagalpur Civil Court, heads the district judiciary in Bhagalpur, the silk city of eastern Bihar. It is led by the District & Sessions Judge and works through Additional District Judges, Civil Judges (Senior and Junior Division) and Judicial Magistrates, with courts at Kahalgaon and a civil court complex at Naugachia. These courts hear civil suits, sessions trials, bail applications, family matters, motor-accident claims and Section 138 cheque-bounce cases. Appeals and revisions lie to the Patna High Court. Every matter carries a CNR number used for online tracking.",
    caseTypes: [
      "Civil Suits & Title Disputes",
      "Criminal Cases & Sessions Trials",
      "Bail Applications",
      "Family & Matrimonial Matters",
      "Motor Accident Claims (MACT)",
      "Cheque Bounce Cases (Section 138 NI Act)",
    ],
    commonProblems:
      "The official portal shows Bhagalpur case status only on a manual search, and never alerts you when the next date changes or an order is uploaded. India Case Status monitors your Bhagalpur matters continuously and messages you on WhatsApp and email whenever the record changes.",
    extraFaqs: [
      { question: "How do I check Bhagalpur court case status online?",
        answer: "Enter your CNR number, case number or party name on India Case Status. Once matched on the official record, the matter is tracked automatically with alerts on any change." },
      { question: "Which High Court hears appeals from Bhagalpur?",
        answer: "Appeals and revisions from the Bhagalpur district judiciary lie to the Patna High Court, and both can be tracked in one dashboard." },
      { question: "Can I track a case from the Kahalgaon or Naugachia court?",
        answer: "Yes. Matters from the courts at Kahalgaon and the civil court complex at Naugachia are tracked the same way as the main Bhagalpur complex, with the same alerts." },
      { question: "How will I know when my next Bhagalpur hearing is?",
        answer: "Your next hearing date appears on your dashboard and hearing calendar, and you receive an alert when a new date is published on the official record." },
    ],
  },
];
